/**
 * What this corpus site serves: its code, research mode, reference grammar, book index and
 * the versions a reader may put on the row — asked for once, held as one value.
 */

import { useEffect, useState } from 'react'
import { getCorpus } from './api'
import type { PickableVersion } from './versionPicker'

/**
 * What this site serves, resolved once — asking per card would be a request per open.
 *
 * Facts from one payload: which corpus, whether it offers research mode, its
 * reference grammar, and where its pages list every book. They arrive together and asking twice would let them disagree when one
 * request failed, which is the mistake `siteBootstrap()` was written to end on
 * the root site — so this hook holds them as one value rather than handing the
 * shell two setters to keep in step. That is why it returns state where the
 * hooks around it take a callback: there is one fact here, with two halves.
 */
export function useCorpusFacts(): CorpusFacts {
  const [facts, setFacts] = useState<CorpusFacts>(NO_CORPUS)
  useEffect(() => {
    getCorpus().then(
      (info) =>
        setFacts({
          code: info.code,
          allowsResearch: info.allow_research,
          grammar: info.reference_grammar,
          bookIndex: info.book_index,
          // Addable only (`can_be_active`: a text version the AI may quote): a commentary is
          // read when the model names it, never part of the row.
          versions: info.versions
            .filter((version) => version.addable)
            .map(({ code, name, language, pinned }) => ({ code, name, language, pinned })),
        }),
      // A corpus that cannot be reached offers nothing: no toggle is the safe
      // drawing, and the relay would refuse the ask anyway.
      () => setFacts(NO_CORPUS),
    )
  }, [])
  return facts
}

export interface CorpusFacts {
  code: string | null
  allowsResearch: boolean
  /** How a reference is written here, which also names the grounded mode: "Bible only". */
  grammar: string | null
  /** Where the pages list every book; null until the corpus answers, and no link is drawn. */
  bookIndex: string | null
  /** Every text version, for the version row: the pinned ones and those the reader may add. */
  versions: PickableVersion[]
}

const NO_CORPUS: CorpusFacts = {
  code: null,
  allowsResearch: false,
  grammar: null,
  bookIndex: null,
  versions: [],
}
